import { Injectable, inject, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  Location,
  MoonShared,
  MoonSharedConfig,
  ProfileApiSource,
  User,
} from '@moon-v2/moon-share';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { combineLatest, firstValueFrom, from, Observable, ReplaySubject } from 'rxjs';
import { map, finalize, filter, switchMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { CountryService } from '@core/services/country-service/country-service';
import { AnalyticsService } from '@core/services/analytics-service/analytics-service';
import { HttpErrorNotifierService } from '@core/services/http-error-notifier/http-error-notifier-service';
import { stripCountryInLocationWhenArrayPresent } from './moonshared-request-interceptors';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly httpErrorNotifier = inject(HttpErrorNotifierService);
  private readonly analyticsService = inject(AnalyticsService);
  private _userProfile = new ReplaySubject<User>(1);
  public readonly userProfile: Observable<User> = this._userProfile.asObservable();
  private countrySelectedService = inject(CountryService);
  readonly user = toSignal(this.userProfile, { initialValue: null });
  readonly roles = computed(() => this.user()?.roles || []);
  // Signal pour suivre l'état d'initialisation de l'auth
  readonly isInitialized = signal(false);
  // Signal pour suivre l'état de déconnexion
  readonly isLoggingOut = signal(false);
  // Signal pour suivre l'état d'initialisation de MoonShared
  private readonly moonSharedInitialized = signal(false);
  // Flag pour éviter les appels multiples au profil
  private profileFetched = false;
  // Mémorise la Promise d'initialisation pour que les guards puissent attendre
  // que les rôles soient chargés, sans redéclencher checkAuth()/le fetch profil.
  private initPromise: Promise<void> | null = null;

  /**
   * Initie le flux d'authentification OIDC
   */
  login(): void {
    this.oidcSecurityService.authorize();
  }

  // ... (autres méthodes inchangées)

  /**
   * Déconnecte l'utilisateur
   */
  logout(): void {
    this.isLoggingOut.set(true);
    this.analyticsService.resetUserId();
    this.oidcSecurityService.logoff().subscribe();
  }

  /**
   * Observable de l'état d'authentification
   */
  get isAuthenticated$(): Observable<boolean> {
    return this.oidcSecurityService.isAuthenticated$.pipe(map((result) => result.isAuthenticated));
  }

  /**
   * Observable des données utilisateur
   */
  get userData$(): Observable<any> {
    return this.oidcSecurityService.userData$;
  }

  /**
   * Récupère le token d'accès
   */
  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  /**
   * Vérifie l'authentification au démarrage de l'application
   */
  checkAuth(): Observable<boolean> {
    return this.oidcSecurityService.checkAuth().pipe(map((result) => result.isAuthenticated));
  }

  /**
   * Récupère les données utilisateur de manière synchrone
   */
  getUserData(): any {
    return this.oidcSecurityService.getUserData();
  }

  /**
   * Initialise le service d'authentification au démarrage de l'application
   * DOIT être appelé dans le ngOnInit du composant racine (App)
   * Retourne une Promise qui se résout quand l'initialisation est complète (auth + MoonShared)
   */
  async initialize(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = new Promise<void>((resolve) => {
      this.oidcSecurityService
        .checkAuth()
        .pipe(
          finalize(() => {
            // Marque l'initialisation de l'auth comme terminée
            this.isInitialized.set(true);
          }),
        )
        .subscribe({
          next: (result) => this.onCheckAuthResult(result, resolve),
          // Sans ce handler, une erreur de `checkAuth()` laissait la Promise en attente pour
          // toujours : `authGuard`/`rolesGuard` (qui l'attendent) bloquaient la navigation
          // sur une page blanche. Cas typique : « could not find matching config for state »,
          // levé quand l'URL porte un `?code=&state=` qui ne correspond plus au `state`
          // stocké (URL de retour Keycloak rechargée, login lancé depuis un autre onglet…).
          error: (error) => {
            console.warn('🔐 checkAuth a échoué, session considérée comme non authentifiée', error);
            this.clearOidcCallbackParams();
            resolve(); // non authentifié → `authGuard` redirige vers /login
          },
        });
    });
    return this.initPromise;
  }

  /**
   * Retire de l'URL les paramètres d'un retour OIDC devenu inutilisable : sans ça, le
   * prochain rechargement relancerait `checkAuth()` sur le même `state` inconnu, et
   * l'erreur se reproduirait en boucle.
   */
  private clearOidcCallbackParams(): void {
    const url = new URL(window.location.href);
    const oidcParams = ['code', 'state', 'session_state', 'iss', 'error', 'error_description'];
    if (!oidcParams.some((param) => url.searchParams.has(param))) return;
    oidcParams.forEach((param) => url.searchParams.delete(param));
    window.history.replaceState(window.history.state, document.title, url.toString());
  }

  private async onCheckAuthResult(
    { isAuthenticated, userData, accessToken }: LoginResponse,
    resolve: () => void,
  ): Promise<void> {
    console.log('🔐 Auth initialized:', {
      isAuthenticated,
      user: userData?.preferred_username || userData?.email,
      hasToken: !!accessToken,
    });

    if (isAuthenticated && accessToken) {
      // Attendre que le profil soit récupéré et que MoonShared soit initialisé
      await this.getProfileWithApiSource();

      // On résout la Promise seulement après que MoonShared soit prêt
      // Si pas de country sélectionné, on résout quand même après un délai
      const maxWait = 5000; // 5 secondes max
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.moonSharedInitialized() || Date.now() - startTime > maxWait) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Redirige vers l'accueil si l'utilisateur est authentifié
      if (this.router.url.includes('/login')) {
        this.router.navigate(['/']);
      }
    } else {
      // Pas authentifié, on résout immédiatement
      resolve();
    }
  }

  async initMoonShare(token: string, inLocation: string) {
    // Éviter les ré-initialisations si déjà fait AVEC LA MÊME LOCATION
    if (this.moonSharedInitialized() && this.currentLocation === inLocation) {
      return;
    }

    try {
      const config: MoonSharedConfig = {
        token: token,
        inLocation: inLocation,
        baseURL: environment.baseUrl,
        debug: false,
        axiosInterceptors: {
          request: {
            onFulfilled: async (requestConfig) => {
              stripCountryInLocationWhenArrayPresent(requestConfig);

              // CIZO uses a dedicated HS256 token — skip Keycloak overwrite
              if (requestConfig.url?.includes(environment.cizoUrl ?? '')) {
                return requestConfig;
              }
              try {
                const freshToken = await firstValueFrom(
                  this.oidcSecurityService.getAccessToken().pipe(filter((t): t is string => !!t)),
                );
                (requestConfig.headers as Record<string, string>)['Authorization'] =
                  `Bearer ${freshToken}`;
              } catch {
                // No token available, let the request proceed as-is
              }
              return requestConfig;
            },
            onRejected: (error) => Promise.reject(error),
          },
          response: {
            onFulfilled: (response) => response,
            onRejected: (error) => {
              const status = error?.response?.status;
              const isCizoUrl = error?.config?.url?.includes(environment.cizoUrl ?? '');
              const isCancelled = error?.code === 'ERR_CANCELED';

              if (status === 401 && !isCizoUrl) {
                this.router.navigate(['/login']);
              } else if (!isCizoUrl) {
                const responseData = error?.response?.data as Record<string, unknown> | undefined;
                const rawDetail = responseData?.['detail'];
                this.httpErrorNotifier.notify({
                  status,
                  detail: typeof rawDetail === 'string' ? rawDetail : undefined,
                  cancelled: isCancelled,
                });
              }

              return Promise.reject(error);
            },
          },
        },
      };

      await MoonShared.initialize(config);
      // Marquer MoonShared comme initialisé
      this.moonSharedInitialized.set(true);
      this.currentLocation = inLocation;
    } catch (error) {
      console.error(`MoonShared initialization failed: ${error}`);
      throw error;
    }
  }

  private currentLocation: string | null = null;

  initReactiveMoonShare() {
    const token$ = this.oidcSecurityService.getAccessToken().pipe(filter((t): t is string => !!t));

    const country$ = this.countrySelectedService.selectedCountry$.pipe(
      filter((c): c is Location => c !== null && !!c.uuid),
    );

    // Pas de take(1) : un user multi-pays doit pouvoir changer de pays plusieurs fois dans
    // la même session. initMoonShare() ignore déjà les appels redondants (même inLocation),
    // et c'est le seul point qui réinitialise MoonShared avec le jeu complet d'intercepteurs
    // (refresh token + gestion 401) — un second point d'entrée sans ces intercepteurs les
    // écraserait silencieusement (cf. AxiosClient.configure(), qui remplace tout).
    combineLatest([token$, country$])
      .pipe(switchMap(([token, country]) => from(this.initMoonShare(token, country.uuid))))
      .subscribe({
        error: (err) => console.error('Error on int MoonShare:', err),
      });
  }

  async getProfileWithApiSource() {
    // Éviter les appels multiples
    if (this.profileFetched || this._userProfile.isStopped) {
      return;
    }

    this.profileFetched = true;

    try {
      const profileApiSource = new ProfileApiSource(environment.baseUrl);
      const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());

      if (token) {
        const user = await profileApiSource.getProfile({ token: token });

        this._userProfile.next(user);
        this.analyticsService.setUserId(user.uuid);
        if (user.position) this.analyticsService.setCustomDimension(1, user.position);

        // NOUVEAU: Initialiser MoonShared IMMÉDIATEMENT avec la location de l'utilisateur
        if (user.location) {
          await this.initMoonShare(token, user.location);
        } else {
          console.warn('⚠️ User has no location, cannot initialize MoonShared');
        }

        // Ensuite on écoute les changements de pays (optionnel, si user change)
        this.initReactiveMoonShare();
      }
    } catch (error) {
      console.log('🚀 ~ AuthService ~ getProfile ~ errors:', error);
      this.profileFetched = false; // Réinitialiser en cas d'erreur pour permettre un retry
    }
  }

  getUserProfile() {
    return this.userProfile;
  }

  getUserRole() {
    return this.userProfile.pipe(map((user) => user.roles));
  }

  /**
   * Vérification niveau service — deuxième ligne de défense après le guard de route
   * et la directive de template (`*hasAnyRole`), avant tout appel API sensible
   * (ex. action rapide sur un contrat). Sémantique OR, cohérente avec `rolesGuard`.
   */
  hasAnyRole(roles: string[]): boolean {
    if (roles.length === 0) return true;
    const userRoles = this.roles();
    return roles.some((role) => userRoles.includes(role));
  }

  /**
   * Pour vérifier l'authentification, utilisez isAuthenticated$ (Observable)
   * Exemple: this.authService.isAuthenticated$.subscribe(isAuth => ...)
   */
}
